import { onMounted, onUnmounted } from "vue";
import {
  captureCorrection,
  physicalRotation,
  type QuarterTurn,
} from "@/utils/imageOrientation";

/** Attente max de la première mesure après l'autorisation iOS. */
const FIRST_READING_TIMEOUT_MS = 500;

interface Tilt {
  beta: number;
  gamma: number;
}

type PermissionRequester = { requestPermission?: () => Promise<string> };

/**
 * Inclinaison de la tablette pendant la prise de vue (caméra intégrée), pour
 * remettre la photo à l'endroit quand l'écran n'a pas suivi la rotation de
 * l'appareil (cf. utils/imageOrientation.ts).
 *
 * Android : les événements arrivent sans autorisation dès le montage. iOS :
 * il faut `requestPermission()` dans un geste utilisateur — demandé au premier
 * déclencheur. Sans capteur ni autorisation, aucune correction (sens de
 * l'écran, comportement historique) ; le bouton « Pivoter » de la barre de
 * sélection reste le filet de sécurité.
 */
export function useDeviceTilt() {
  let last: Tilt | null = null;
  let waiters: (() => void)[] = [];
  let permissionAsked = false;

  const onOrientation = (e: DeviceOrientationEvent) => {
    if (e.beta === null || e.gamma === null) return;
    last = { beta: e.beta, gamma: e.gamma };
    waiters.forEach((resolve) => resolve());
    waiters = [];
  };

  onMounted(() => {
    window.addEventListener("deviceorientation", onOrientation);
  });
  onUnmounted(() => {
    window.removeEventListener("deviceorientation", onOrientation);
    waiters.forEach((resolve) => resolve());
    waiters = [];
  });

  /** À appeler depuis le geste de capture (clic) : autorisation iOS au besoin. */
  async function ensureReading(): Promise<void> {
    if (last || permissionAsked) return;
    permissionAsked = true;
    const requester = (
      typeof DeviceOrientationEvent !== "undefined"
        ? DeviceOrientationEvent
        : undefined
    ) as PermissionRequester | undefined;
    if (typeof requester?.requestPermission !== "function") return;
    try {
      if ((await requester.requestPermission()) !== "granted") return;
    } catch {
      return;
    }
    await new Promise<void>((resolve) => {
      waiters.push(resolve);
      setTimeout(resolve, FIRST_READING_TIMEOUT_MS);
    });
  }

  /** Quart de tour (antihoraire) à appliquer à l'image qu'on capture maintenant. */
  function currentCorrection(): QuarterTurn {
    const physical = last ? physicalRotation(last.beta, last.gamma) : null;
    return captureCorrection(physical, screenAngle());
  }

  return { ensureReading, currentCorrection };
}

function screenAngle(): number {
  const angle = window.screen?.orientation?.angle;
  if (typeof angle === "number") return angle;
  // Ancien Safari : `window.orientation` (−90 | 0 | 90 | 180).
  const legacy = (window as { orientation?: number }).orientation;
  return typeof legacy === "number" ? legacy : 0;
}
